import { createColumnHelper, flexRender, getCoreRowModel, useReactTable } from "@tanstack/react-table";
import styles from "./AnimalEpdTable.module.scss";
import { EmptyStatesAnimalEpdTable } from "components/EmptyStates/AnimalEpdTable";
import { AmericanAngusEpdQuery, NeogenEpdQuery, useAmericanAngusEpdQuery, useNeogenEpdQuery } from "generated/graphql";

type Epd = {
  name: string;
  epd: number | null;
  pct: number | null;
  acc: number | null;
};

type AmericanAngusEpd = AmericanAngusEpdQuery["americanAngusEpd"];
type NeogenEpd = NeogenEpdQuery["neogenEpd"];

export const camelCaseToLabel = (name: string): string => {
  const words = name.replace(/([A-Z])/g, " $1").trim();
  const formattedName = words.charAt(0).toUpperCase() + words.slice(1);
  return formattedName;
};

export const AnimalEpdTable = ({ animalId }) => {
  const transformEpdData = (data: AmericanAngusEpd | NeogenEpd): Epd[] => {
    const transformedEpdData: Epd[] = [];
    const processedBaseNames: { [key: string]: Epd } = {};

    for (const key in data) {
      if (key === "__typename") continue;

      const typeMatch = key.match(/(Acc|Epd|Pct)$/);
      const baseName = typeMatch ? key.substring(0, key.length - typeMatch[0].length) : key;
      const formattedBaseName = camelCaseToLabel(baseName);

      if (!processedBaseNames[baseName]) {
        processedBaseNames[baseName] = { name: formattedBaseName, epd: null, pct: null, acc: null };
        transformedEpdData.push(processedBaseNames[baseName]);
      }

      const value = parseFloat(data[key] as string);
      if (typeMatch) {
        const type = typeMatch[0];
        if (type === "Epd") processedBaseNames[baseName].epd = value;
        else if (type === "Pct") processedBaseNames[baseName].pct = value;
        else if (type === "Acc") processedBaseNames[baseName].acc = value;
      } else {
        processedBaseNames[baseName].epd = value;
      }
    }

    return transformedEpdData;
  };

  let epdData: Epd[] = [];

  const { data: americanAngusEpdData } = useAmericanAngusEpdQuery({
    variables: {
      id: animalId,
    },
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-only",
  });

  const { data: neogenEpdData } = useNeogenEpdQuery({
    variables: {
      id: animalId,
    },
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-only",
  });

  if (americanAngusEpdData && americanAngusEpdData.americanAngusEpd) {
    epdData = transformEpdData(americanAngusEpdData.americanAngusEpd);
  } else if (neogenEpdData && neogenEpdData.neogenEpd) {
    epdData = transformEpdData(neogenEpdData.neogenEpd);
  }

  const columnHelper = createColumnHelper<Epd>();

  const columns = [
    columnHelper.accessor("name", {
      id: "name",
      cell: (info) => info.getValue(),
      header: () => <span>Name</span>,
    }),
    columnHelper.accessor((row) => row.epd, {
      id: "epd",
      header: () => <span>Value</span>,
      cell: (info) => (info.getValue() != null ? info.getValue() : "-"),
    }),
    columnHelper.accessor("pct", {
      id: "pct",
      header: () => <span>Percentile</span>,
      cell: (info) => (info.getValue() != null ? info.getValue() : "-"),
    }),
    columnHelper.accessor("acc", {
      id: "acc",
      header: () => <span>Accuracy</span>,
      cell: (info) => (info.getValue() != null ? info.getValue() : "-"),
    }),
  ];

  const table = useReactTable({
    data: epdData,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <>
      {epdData.length > 0 ? (
        <table className={styles.table}>
          <thead className={styles.table__header}>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr className={styles.table__header_row} key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th className={styles.table__header_cell} key={header.id}>
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className={styles.table__body}>
            {table.getRowModel().rows.map((row) => (
              <tr className={styles.table__body_row} key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <td className={styles.table__body_cell} key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <EmptyStatesAnimalEpdTable />
      )}
    </>
  );
};
